/**
 * BudgetPulseNudges — multi-tone Command Center card listing budget
 * milestones across active projects.
 *
 * Severity (most urgent first):
 *   - overdrawn               (red) → over construction + contingency
 *   - pct_100                 (red) → 100% of budget
 *   - contingency_activated   (orange) → tapped contingency reserve
 *   - pct_90                  (orange) → 90% spent
 *   - pct_75                  (yellow) → 75% spent
 *   - pct_50                  (green) → halfway
 *   - pct_25                  (green) → first quarter milestone
 *
 * Data feed: GET /api/users/{user_id}/budget-milestones
 * Dismiss:   POST /api/budget-milestones/{id}/dismiss
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Bell, TrendingUp, AlertTriangle, AlertOctagon, ShieldAlert, Gauge, X, Loader2,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const KIND_CONFIG = {
  overdrawn: {
    icon: AlertOctagon,
    iconBg: '#dc2626',
    label: 'Overdrawn',
    pillBg: 'bg-red-100', pillText: 'text-red-800', pillBorder: 'border-red-300',
    body: (m) =>
      `Total drawn of $${(m.total_spent_at_fire || 0).toLocaleString()} exceeds your $${(m.construction_budget_at_fire || 0).toLocaleString()} construction + $${(m.contingency_budget_at_fire || 0).toLocaleString()} contingency. You need at least $${(m.overdrawn_amount_at_fire || 0).toLocaleString()} more to finish.`,
  },
  pct_100: {
    icon: AlertOctagon,
    iconBg: '#dc2626',
    label: '100% drawn',
    pillBg: 'bg-red-100', pillText: 'text-red-800', pillBorder: 'border-red-300',
    body: (m) =>
      `You've drawn 100% of your $${(m.construction_budget_at_fire || 0).toLocaleString()} construction budget. Any additional draws tap your contingency reserve.`,
  },
  contingency_activated: {
    icon: ShieldAlert,
    iconBg: '#f97316',
    label: 'Contingency activated',
    pillBg: 'bg-orange-100', pillText: 'text-orange-900', pillBorder: 'border-orange-300',
    body: () =>
      `You've started tapping your contingency budget. Review which phases overran to see if it's a one-time hit or a trend.`,
  },
  pct_90: {
    icon: AlertTriangle,
    iconBg: '#f97316',
    label: '90% drawn',
    pillBg: 'bg-orange-100', pillText: 'text-orange-900', pillBorder: 'border-orange-300',
    body: (m) =>
      `Only $${((m.construction_budget_at_fire || 0) - (m.total_spent_at_fire || 0)).toLocaleString()} left before you tap contingency. Lock in remaining phase costs now.`,
  },
  pct_75: {
    icon: Gauge,
    iconBg: '#eab308',
    label: '75% drawn',
    pillBg: 'bg-yellow-100', pillText: 'text-yellow-900', pillBorder: 'border-yellow-300',
    body: () =>
      `Three-quarters of the construction budget drawn. Review unbilled phases for anything running hot.`,
  },
  pct_50: {
    icon: TrendingUp,
    iconBg: '#10b981',
    label: '50% drawn',
    pillBg: 'bg-emerald-100', pillText: 'text-emerald-900', pillBorder: 'border-emerald-300',
    body: () => `Halfway through your construction budget. Solid pacing — keep tracking actuals.`,
  },
  pct_25: {
    icon: TrendingUp,
    iconBg: '#10b981',
    label: '25% drawn',
    pillBg: 'bg-emerald-100', pillText: 'text-emerald-900', pillBorder: 'border-emerald-300',
    body: () => `First quarter of construction budget drawn. Solid early-stage pacing.`,
  },
};

// Tonal gradient picks the most severe alert color in the list
const SEVERITY_RANK = [
  'overdrawn', 'pct_100', 'contingency_activated', 'pct_90', 'pct_75', 'pct_50', 'pct_25',
];
function headerGradientFor(milestones) {
  const top = SEVERITY_RANK.find((k) => milestones.some((m) => m.kind === k));
  if (top === 'overdrawn' || top === 'pct_100') {
    return 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'; // red wash
  }
  if (top === 'contingency_activated' || top === 'pct_90') {
    return 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)'; // orange wash
  }
  if (top === 'pct_75') {
    return 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)'; // yellow wash
  }
  return 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)'; // emerald wash
}

export const BudgetPulseNudges = ({ userId }) => {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [currentState, setCurrentState] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dismissingId, setDismissingId] = useState(null);
  // Collapse state persists per-user across reloads
  const COLLAPSE_KEY = `budget-pulse-collapsed:${userId || 'anon'}`;
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch (_) { return false; }
  });

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    api
      .get(`/users/${userId}/budget-milestones`)
      .then((r) => {
        if (!alive) return;
        setMilestones(r.data?.milestones || []);
        setCurrentState(r.data?.current_state || []);
      })
      .catch(() => alive && setMilestones([]))
      .finally(() => alive && setLoaded(true));
    return () => { alive = false; };
  }, [userId]);

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };

  const handleClear = async (id) => {
    setDismissingId(id);
    try {
      await api.post(`/budget-milestones/${id}/dismiss`, { user_id: userId });
      setMilestones((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clear notification');
    } finally {
      setDismissingId(null);
    }
  };

  if (!loaded) return null;
  if (milestones.length === 0 && currentState.length === 0) return null;

  const gradient = milestones.length > 0
    ? headerGradientFor(milestones)
    : 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)';

  // Summary text for collapsed state
  const summaryText = milestones.length > 0
    ? `${milestones.length} alert${milestones.length === 1 ? '' : 's'} · ${currentState.length} project${currentState.length === 1 ? '' : 's'}`
    : `${currentState.length} project${currentState.length === 1 ? '' : 's'} tracked`;

  return (
    <Card
      className="mb-4 overflow-hidden border-0 shadow-md"
      data-testid="budget-pulse-nudges"
      style={{ background: gradient }}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        className="w-full px-4 py-3 flex items-center gap-2 border-b border-black/10 hover:bg-black/5 transition text-left"
        data-testid="budget-pulse-toggle"
        aria-expanded={!isCollapsed}
      >
        <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
          <Bell className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-900">
            Budget Drawn
            {milestones.length > 0 && ` · ${milestones.length} to review`}
          </p>
          {isCollapsed && (
            <p className="text-[11px] text-slate-700 mt-0.5">{summaryText}</p>
          )}
        </div>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-slate-700 flex-shrink-0" />
        ) : (
          <ChevronUp className="w-4 h-4 text-slate-700 flex-shrink-0" />
        )}
      </button>
      {!isCollapsed && (
        <div className="divide-y divide-black/10 bg-white/40">
          {/* Active (not-dismissed) milestone alerts come first */}
          {milestones.slice(0, 6).map((m) => {
            const cfg = KIND_CONFIG[m.kind] || KIND_CONFIG.pct_25;
            const Icon = cfg.icon;
            return (
              <div
                key={m.id}
                className="px-4 py-3 flex items-start gap-3"
                data-testid={`budget-milestone-${m.id}`}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: cfg.iconBg }}
                >
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="text-sm font-bold text-slate-900 truncate">{m.project_name}</p>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${cfg.pillBg} ${cfg.pillText} ${cfg.pillBorder}`}
                    >
                      {cfg.label}
                    </span>
                  </div>
                  <p className="text-xs text-slate-700 leading-snug">{cfg.body(m)}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button
                    size="sm"
                    className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-8"
                    onClick={() => navigate(`/project/${m.project_id}`)}
                    data-testid={`budget-milestone-open-${m.id}`}
                  >
                    Open project
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white hover:bg-slate-50 text-slate-700 border-slate-300 text-xs h-8 px-2"
                    onClick={() => handleClear(m.id)}
                    disabled={dismissingId === m.id}
                    data-testid={`budget-milestone-clear-${m.id}`}
                    title="Clear this notification — won't show again"
                  >
                    {dismissingId === m.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
          {milestones.length > 6 && (
            <p className="px-4 py-2 text-[11px] text-slate-600 text-center">
              + {milestones.length - 6} more
            </p>
          )}

          {/* Quiet "current % drawn" rows for every active project */}
          {currentState.length > 0 && (
            <div className="bg-slate-50/80">
              <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                Live draws per project
              </div>
              {currentState.map((s) => {
                const pct = Math.round((s.pct_spent || 0) * 100);
                const barColor =
                  pct >= 100 ? 'bg-red-500' :
                  pct >= 90 ? 'bg-orange-500' :
                  pct >= 75 ? 'bg-yellow-500' :
                  'bg-emerald-500';
                return (
                  <button
                    key={s.project_id}
                    onClick={() => navigate(`/project/${s.project_id}`)}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-white/60 transition text-left"
                    data-testid={`budget-state-${s.project_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-slate-800 truncate">
                          {s.project_name}
                        </p>
                        <span className="text-[11px] font-bold text-slate-700 ml-2 flex-shrink-0">
                          {pct}% drawn
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${barColor} rounded-full transition-all`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-slate-500 flex-shrink-0 tabular-nums">
                          ${(s.total_spent || 0).toLocaleString()} / ${(s.construction_budget || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default BudgetPulseNudges;
